<template>
  <div data-private class="flex h-full">

    <!-- Sidebar -->
    <PageSidebar title="Performance Domains" :items="sidebarItems">
      <LoadingIndicator v-if="loading" message="Loading domains..." />
      <div v-else-if="error" class="p-4">
        <div class="bg-red-50 border border-red-200 rounded-md p-3">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>
      </div>
    </PageSidebar>

    <!-- Main Content -->
    <div class="flex-1 h-full overflow-x-auto bg-gray-100">
      <Impact_Analytics v-if="showImpactAnalytics" />
      <div v-else class="p-6 space-y-4">

        <InsightsNew_MetricView
          :page-type="pageType"
          :metric-ids="metricIds"
          :performance-domain-ids="performanceDomainIds"
          :is-domain="isDomain"
          :entity-ids="entityIds"
        />

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FEATURES } from '@/constants/features';
import PageSidebar from '../widgetcomponents/library/PageSidebar.vue';
import LoadingIndicator from '../widgetcomponents/library/LoadingIndicator.vue';
import InsightsNew_MetricView from './InsightsNew_MetricView.vue';
import Impact_Analytics from '../Impact_Analytics/Impact_Analytics.vue';

export default {
  name: 'InsightsNew',
  components: { PageSidebar, LoadingIndicator, InsightsNew_MetricView, Impact_Analytics },
  props: {
    entityIds: { type: Array, default: () => [] },
    performanceDomainId: { type: Number, default: null },
    metricId: { type: Number, default: null },
    showImpactAnalytics: { type: Boolean, default: false },
  },
  data() {
    return {
      domains: [],
      loading: false,
      error: null,
    };
  },
  computed: {
    pageType() {
      if (this.metricId) return 'metric_page';
      if (this.performanceDomainId) return 'domain_page';
      return 'overall_performance';
    },
    isDomain() {
      return this.pageType !== 'metric_page';
    },
    selectedDomain() {
      return this.domains.find(d => d.id === this.performanceDomainId);
    },
    metricIds() {
      if (this.metricId) return [this.metricId];
      if (this.selectedDomain) {
        const primary = (this.selectedDomain.composable_metrics || []).find(m => m.primary_metric);
        return primary ? [primary.metric_id] : [];
      }
      return [];
    },
    performanceDomainIds() {
      if (this.performanceDomainId) return [this.performanceDomainId];
      return [];
    },
    entitySuffix() {
      return this.entityIds.length > 0 ? `/${this.entityIds.join('/')}` : '';
    },
    sidebarItems() {
      const suffix = this.entitySuffix;
      return [
        {
          id: 0,
          label: 'Overall Performance',
          to: `/insight${suffix}`,
        },
        ...this.domains.map(domain => ({
          id: domain.id,
          label: domain.name,
          to: `/insight/domain/${domain.id}${suffix}`,
          icon: domain.has_custom_page ? ['fas', 'wand-magic-sparkles'] : null,
          iconClass: 'text-blue-500 text-[10px]',
          iconTitle: 'Custom page layout',
          children: (domain.composable_metrics || [])
            .map(link => ({
              id: link.metric_id,
              label: link.metric_name,
              to: `/insight/metric/${link.metric_id}${suffix}`,
              badge: link.primary_metric ? 'Primary' : null,
              badgeClass: 'bg-primary-400 text-white',
              icon: link.has_custom_page ? ['fas', 'wand-magic-sparkles'] : null,
              iconClass: 'text-blue-500 text-[10px]',
              iconTitle: 'Custom page layout',
            }))
            .sort((a, b) => {
              if (a.badge === 'Primary' && b.badge !== 'Primary') return -1;
              if (b.badge === 'Primary' && a.badge !== 'Primary') return 1;
              return a.label.localeCompare(b.label);
            }),
        })),
        {
          id: 'impact-analytics-header',
          label: 'Impact Analytics',
          header: true,
          canAccess: FEATURES.MODULE_PORTFOLIO,
        },
        {
          id: 'impact-analytics-studies',
          label: 'Study Builder',
          to: '/insight/impact-analytics/study-builder',
          canAccess: FEATURES.MODULE_PORTFOLIO,
        },
        {
          id: 'impact-analytics-wizard',
          label: 'Study Wizard',
          to: '/beta/agent',
          canAccess: FEATURES.MODULE_PORTFOLIO,
        },
      ];
    },
  },
  mounted() {
    this.fetchDomains();
  },
  methods: {
    async fetchDomains() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get('/api/performancedomains/');
        this.domains = response.data;
      } catch (err) {
        console.error('Error fetching performance domains:', err);
        this.error = 'Failed to load performance domains';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
